const DEFAULT_FILTER_TYPE = "default";
const ICON_ACTIVE_ITEM_KEY = "__activeItemName";

const isEmptyValue = (value) => {
  if (value === undefined || value === null || value === "") {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    return Object.values(value).every(isEmptyValue);
  }

  return false;
};

const normalizePayloadValue = (value) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return value;
};

const getFilterType = (filterOption) =>
  filterOption?.filterType || filterOption?.type || DEFAULT_FILTER_TYPE;

const getDefaultFilterName = (filterOption) =>
  filterOption?.name || filterOption?.hintname || filterOption?.type;

const getIconFilterKey = (filterOption) =>
  filterOption?.name || filterOption?.type || "iconFilter";

const getIconPayloadKeys = (filterOption) => ({
  selectedValuesKey:
    filterOption?.selectedValuesKey || filterOption?.valueKey || "statue",
  activeItemKey:
    filterOption?.activeItemKey || filterOption?.groupKey || "posture",
});

const getDefaultIconItem = (filterOption) =>
  filterOption?.options?.find(
    (option) => option.isSelected === true || option.isSelected === "true"
  ) ||
  filterOption?.options?.[0] ||
  null;

const getDefaultIconSelectedValues = (item) =>
  item?.subfilter
    ?.flatMap((subfilter) => subfilter.fields || [])
    ?.filter((field) => field.type === "checkbox")
    ?.flatMap((field) => field.values || []) || [];

const buildIconFilterPayload = (selectedFilters, filterOption) => {
  const filterKey = getIconFilterKey(filterOption);
  const hasAppliedIconSelection = Object.prototype.hasOwnProperty.call(
    selectedFilters,
    filterKey
  );
  const iconSelections = selectedFilters[filterKey] || {};
  const defaultIconItem = getDefaultIconItem(filterOption);

  const fallbackSelectedEntry = Object.entries(iconSelections).find(
    ([key, value]) => key !== ICON_ACTIVE_ITEM_KEY && !isEmptyValue(value)
  );
  const activeItemName =
    iconSelections[ICON_ACTIVE_ITEM_KEY] ||
    fallbackSelectedEntry?.[0] ||
    defaultIconItem?.imageName;
  const selectedValues = hasAppliedIconSelection
    ? iconSelections[activeItemName]
    : getDefaultIconSelectedValues(defaultIconItem);

  if (
    !activeItemName ||
    (!hasAppliedIconSelection && isEmptyValue(selectedValues))
  ) {
    return null;
  }

  const { selectedValuesKey, activeItemKey } = getIconPayloadKeys(filterOption);

  return {
    filterType: getFilterType(filterOption),
    [selectedValuesKey]: normalizePayloadValue(selectedValues),
    [activeItemKey]: activeItemName,
  };
};

const filterPayloadBuilders = {
  iconFilter: buildIconFilterPayload,
};

const getSpecialFilterKeys = (metaData = []) =>
  metaData
    .filter((filterOption) => filterPayloadBuilders[getFilterType(filterOption)])
    .map(getIconFilterKey);

const buildDefaultFilterPayload = (selectedFilters = {}, metaData = []) => {
  const filterPayload = {
    filterType: DEFAULT_FILTER_TYPE,
  };

  const specialFilterKeys = new Set(getSpecialFilterKeys(metaData));
  const defaultFilterNames = metaData
    .filter(
      (filterOption) =>
        filterOption?.isActive &&
        !filterPayloadBuilders[getFilterType(filterOption)]
    )
    .map(getDefaultFilterName)
    .filter(Boolean);

  defaultFilterNames.forEach((filterName) => {
    const value = selectedFilters[filterName];

    if (!isEmptyValue(value)) {
      filterPayload[filterName] = normalizePayloadValue(value);
    }
  });

  Object.entries(selectedFilters).forEach(([filterName, value]) => {
    if (
      filterPayload[filterName] !== undefined ||
      specialFilterKeys.has(filterName) ||
      isEmptyValue(value)
    ) {
      return;
    }

    filterPayload[filterName] = normalizePayloadValue(value);
  });

  return filterPayload;
};

export const buildProductsFilterPayload = (
  selectedFilters = {},
  metaData = []
) => {
  const defaultPayload = buildDefaultFilterPayload(selectedFilters, metaData);

  const customPayload = metaData
    .filter((filterOption) => filterOption?.isActive)
    .map((filterOption) => {
      const builder = filterPayloadBuilders[getFilterType(filterOption)];
      return builder?.(selectedFilters, filterOption);
    })
    .find(Boolean);

  if (!customPayload) {
    return defaultPayload;
  }

  return {
    ...defaultPayload,
    ...customPayload,
    filterType: customPayload.filterType || DEFAULT_FILTER_TYPE,
  };
};
